/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [true]
*/

import { getFilterElementsDefault, useWorkplaceState } from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addTradeOrg,
    changeTradeOrg,
    deleteTradeOrg,
    getTradeOrg,
    getTradeOrgs,
    getTradeOrgsExcel
} from 'axios/tradeOrgsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    TradeOrg, TradeOrgFilterType,
    TradeOrgTableItem
} from 'models/api/tradeOrg';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { checkboxFormatter, deleteActionBtnFormatter } from 'utils/columnFormatters';
import TradeOrgChangeModal from './Modal/TradeOrgChangeModal';
import TradeOrgAddButton from './TradeOrgAddButton';
import TradeOrgFilter from './TradeOrgFilter';

const TradeOrgs = () => {
    const { hasRole } = useAuth();
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const api = useApi();

    const [isModalVisible, setIsModalVisible] = useState(false);

    useEffect(() => {
        queryClient.invalidateQueries([['tradeOrgs']]);
    }, [i18n.language]);

    return (
        <BOWorkplace<TradeOrgTableItem, TradeOrg, TradeOrgFilterType, FormData, FormData>
            pageTitle="BackOfficeTradeOrgs"
            filterParams={{
                fullName: '',
            }}
            initColumns={[
                { key: 'id', name: <Trans i18nKey={'BackOfficeId'} /> },
                {
                    key: 'isActive',
                    name: <Trans i18nKey={'BackOfficeIsActive'} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    formatter: checkboxFormatter('isActive'),
                },
                { key: 'fullName', name: <Trans i18nKey={'BackOfficeTradeOrg'} /> },
                { key: 'integrationType', name: <Trans i18nKey={'BackOfficeIntegrationType'} /> },
                { key: 'url', name: <Trans i18nKey={'BackOfficeTradeOrgUrl'} /> },
                { key: 'dsc', name: <Trans i18nKey={'BackOfficeDescription'} /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: deleteActionBtnFormatter(hasRole('tradeOrgsDelete')),
                },
            ]}
            storagePath="tradeOrgs/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'id', name: t('BackOfficeId') },
                { id: 'fullName', name: t('BackOfficeTradeOrg') },
            ])}
            showModal={hasRole('tradeOrgsGetById') ? () => setIsModalVisible(true) : undefined}
            filter={<TradeOrgFilter />}
            modal={{
                component: isModalVisible && (
                    <TradeOrgChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                fetchCurrentItemQueryFn: (id) => getTradeOrg(api, id),
                changeMutationFn: (item) => changeTradeOrg(api, item),
                addMutationFn: (item) => addTradeOrg(api, item),
            }}
            deleteMutationFn={(id) => deleteTradeOrg(api, id)}
            fetchQueryFn={(filter) => getTradeOrgs(api, filter)}
            buttons={[
                hasRole('tradeOrgsCreate') ? (
                    <TradeOrgAddButton key="1" showModal={() => setIsModalVisible(true)} />
                ) : null,
            ]}
            queryKey={['tradeOrgs']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getTradeOrgsExcel(api, filter)}
            showExcelBtn={hasRole('tradeOrgsExcel')}
        />
    );
};

const useTradeOrgWorkplaceState = () =>
    useWorkplaceState<TradeOrgTableItem, TradeOrg, FormData, FormData>();

export default TradeOrgs;

export { useTradeOrgWorkplaceState };
